/**
 * useMyProfile — Loading + state + handlers für MyProfile-Seite.
 */
import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { employeesAPI, changeRequestsAPI, notificationsAPI } from '../../lib/api';

export function useMyProfile() {
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [myRequests, setMyRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(true);

  // Change request dialog — Multi-Feld
  const [changeDialogOpen, setChangeDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [fieldValues, setFieldValues] = useState({}); // { "category:field": { category, field_name, value } }
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // History dialog
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);

  // Notification settings
  const [notificationSettings, setNotificationSettings] = useState({
    workflow_tasks: true,
    change_requests: true,
    vacation_requests: true,
    correction_requests: true,
    shift_changes: true,
  });
  const [loadingNotifSettings, setLoadingNotifSettings] = useState(true);
  const [savingNotifSettings, setSavingNotifSettings] = useState(false);

  const loadProfile = useCallback(async () => {
    try {
      const response = await employeesAPI.getMyProfile();
      setEmployee(response.data);
    } catch (error) {
      console.error('Error loading profile:', error);
      toast.error('Profil konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMyRequests = useCallback(async () => {
    try {
      const response = await changeRequestsAPI.getMyRequests();
      setMyRequests(response.data);
    } catch (error) {
      console.error('Error loading change requests:', error);
    } finally {
      setLoadingRequests(false);
    }
  }, []);

  const loadNotificationSettings = useCallback(async () => {
    try {
      const response = await notificationsAPI.getSettings();
      setNotificationSettings(response.data);
    } catch (error) {
      console.error('Error loading notification settings:', error);
    } finally {
      setLoadingNotifSettings(false);
    }
  }, []);

  const updateNotificationSetting = async (key, value) => {
    setSavingNotifSettings(true);
    try {
      await notificationsAPI.updateSettings({ [key]: value });
      setNotificationSettings((prev) => ({ ...prev, [key]: value }));
      toast.success('Einstellung gespeichert');
    } catch (error) {
      console.error('Error updating notification setting:', error);
      toast.error('Fehler beim Speichern');
      setNotificationSettings((prev) => ({ ...prev, [key]: !value }));
    } finally {
      setSavingNotifSettings(false);
    }
  };

  useEffect(() => {
    loadProfile();
    loadMyRequests();
    loadNotificationSettings();
  }, [loadProfile, loadMyRequests, loadNotificationSettings]);

  const openChangeDialog = (category, fieldName) => {
    setSelectedCategory(category);
    // Standardmäßig das angeklickte Feld vorauswählen — weitere Felder kann der
    // Nutzer im Dialog per Checkbox ergänzen.
    if (fieldName) {
      setFieldValues({ [`${category}:${fieldName}`]: { category, field_name: fieldName, value: '' } });
    } else {
      setFieldValues({});
    }
    setReason('');
    setChangeDialogOpen(true);
  };

  const handleSubmitChangeRequest = async () => {
    const items = Object.values(fieldValues)
      .filter((v) => v && v.value != null && String(v.value).trim() !== '')
      .map((v) => ({
        category: v.category,
        field_name: v.field_name,
        current_value: employee?.[v.field_name] || '',
        new_value: String(v.value).trim(),
      }));

    if (items.length === 0) {
      toast.error('Bitte mindestens einen neuen Wert eingeben');
      return;
    }

    setSubmitting(true);
    try {
      if (items.length === 1) {
        await changeRequestsAPI.create({
          category: items[0].category,
          field_name: items[0].field_name,
          current_value: items[0].current_value,
          new_value: items[0].new_value,
          reason: reason.trim() || null,
        });
      } else {
        await changeRequestsAPI.createBulk({
          items,
          reason: reason.trim() || null,
        });
      }
      toast.success(
        items.length === 1
          ? 'Änderungsantrag erfolgreich eingereicht'
          : `${items.length} Änderungsanträge erfolgreich eingereicht`
      );
      setChangeDialogOpen(false);
      setFieldValues({});
      setReason('');
      loadMyRequests();
    } catch (error) {
      console.error('Error submitting change request:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Einreichen');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteRequest = async (requestId) => {
    if (!window.confirm('Möchten Sie diesen Antrag wirklich zurückziehen?')) return;
    try {
      await changeRequestsAPI.delete(requestId);
      toast.success('Antrag zurückgezogen');
      loadMyRequests();
    } catch (error) {
      console.error('Error deleting request:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Zurückziehen des Antrags');
    }
  };

  const openHistoryDialog = (request) => {
    setSelectedRequest(request);
    setHistoryDialogOpen(true);
  };

  return {
    employee, loading, myRequests, loadingRequests,
    changeDialogOpen, setChangeDialogOpen,
    selectedCategory, setSelectedCategory,
    fieldValues, setFieldValues,
    reason, setReason,
    submitting,
    historyDialogOpen, setHistoryDialogOpen,
    selectedRequest,
    notificationSettings, loadingNotifSettings, savingNotifSettings,
    updateNotificationSetting,
    openChangeDialog, handleSubmitChangeRequest,
    handleDeleteRequest, openHistoryDialog,
  };
}
